import { useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WA_ADMIN } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { MessageCircle } from "lucide-react";

const GROUPS = [
  {
    title: "Umum",
    items: [
      { q: "Apa itu Travele?", a: "Travele adalah Digital Terminal dan marketplace yang mempertemukan penyedia Travel Door to Door serta Charter Drop Off dengan penumpang antarkota. Anda bisa membandingkan jadwal, harga, armada, dan sisa kursi dari banyak driver dalam satu halaman." },
      { q: "Apakah Travele perusahaan travel?", a: "Bukan. Travele adalah perantara digital. Layanan transportasi diberikan langsung oleh Driver atau pemilik travel; Travele menampilkan jadwal, menerima order, dan membantu komunikasi melalui Admin." },
      { q: "Apa bedanya Travel Door to Door dan Charter Drop Off?", a: "Door to Door adalah perjalanan antarkota dengan penjemputan dan pengantaran sesuai area layanan driver (berbagi kendaraan). Charter Drop Off adalah sewa satu kendaraan penuh untuk perjalanan pribadi/rombongan." },
    ],
  },
  {
    title: "Pemesanan",
    items: [
      { q: "Bagaimana cara memesan travel?", a: "Cari rute (asal, tujuan, tanggal), pilih jadwal, isi data pemesanan. Sistem membuat nomor order lalu mengarahkan Anda ke WhatsApp Admin resmi untuk konfirmasi ketersediaan." },
      { q: "Apakah saya bisa langsung menghubungi driver?", a: "Tidak pada tahap awal. Semua pemesanan dikelola oleh Admin agar aman dan tercatat. Nomor driver hanya diberikan bila diperlukan untuk pelaksanaan perjalanan sesuai kebijakan Travele." },
      { q: "Ke mana pesanan saya diarahkan?", a: "Ke satu nomor WhatsApp Admin resmi Travele: 0811-2828-852. Hati-hati terhadap nomor tidak resmi." },
      { q: "Apakah mengirim formulir sudah menjamin kursi?", a: "Belum. Pesanan dianggap sah setelah dikonfirmasi Admin. Ketersediaan kursi diperiksa terlebih dahulu bersama driver." },
      { q: "Apakah jam keberangkatan bersifat pasti?", a: "Tidak. Semua jam keberangkatan adalah ESTIMASI. Karena layanan Door to Door menjemput penumpang di beberapa lokasi sebelum berangkat, waktu bisa lebih cepat atau lebih lambat dari jadwal akibat lalu lintas, jarak antar titik jemput, cuaca, atau situasi di lapangan. Selisih waktu adalah hal yang wajar." },
      { q: "Apa itu Tim Langsir dan apakah ada biaya tambahan?", a: "Tim Langsir membantu proses penjemputan/pengantaran menggunakan armada lain bila diperlukan agar waktu tunggu lebih singkat. Seluruh biayanya ditanggung driver/mitra travel dan TIDAK menambah biaya Anda — penumpang tetap membayar sesuai harga yang dikonfirmasi di awal." },
      { q: "Berapa lama Driver menunggu & kapan saya harus siap?", a: "Penumpang wajib siap minimal 15 menit sebelum estimasi penjemputan dengan nomor WhatsApp aktif. Driver menunggu maksimal 10 menit sejak tiba; bila tidak dapat dihubungi atau tidak di lokasi, Driver berhak melanjutkan perjalanan. Selengkapnya di Syarat & Ketentuan Penumpang." },
    ],
  },
  {
    title: "Pembayaran & Harga",
    items: [
      { q: "Siapa yang menentukan harga tiket?", a: "Harga ditentukan oleh masing-masing Driver. Travele hanya menampilkannya secara transparan sebelum Anda memesan." },
      { q: "Bagaimana cara pembayaran?", a: "Dua pilihan: langsung kepada Driver, atau melalui Platform (transfer/QRIS/virtual account bila tersedia). Pastikan hanya membayar ke kanal resmi yang dikonfirmasi Admin." },
      { q: "Apakah ada biaya untuk penumpang?", a: "Anda membayar harga tiket sesuai yang ditampilkan. Biaya tambahan hanya berlaku bila diinformasikan dan Anda setujui sebelumnya." },
    ],
  },
  {
    title: "Driver & Komisi",
    items: [
      { q: "Bagaimana cara menjadi Driver?", a: "Daftar dengan nomor WhatsApp aktif, lengkapi profil usaha & armada, lalu tunggu verifikasi Admin. Setelah terverifikasi, Anda bisa menerbitkan jadwal di Digital Terminal." },
      { q: "Berapa komisi platform?", a: "Komisi Travele adalah 20% dari nilai tiket yang Anda tetapkan, hanya untuk order yang berasal dari platform. Bila bayar langsung ke Driver, komisi menjadi tagihan Driver." },
      { q: "Apa itu Sponsor?", a: "Driver dapat membeli iklan Sponsor untuk menempati maksimal 3 posisi teratas pada suatu rute. Posisi Sponsor ditandai jelas dan terpisah dari hasil organik." },
    ],
  },
  {
    title: "Rating & Pengaduan",
    items: [
      { q: "Siapa yang bisa memberi rating?", a: "Hanya penumpang dengan order yang sudah Selesai. Rating mencakup ketepatan waktu, kebersihan, keramahan, kenyamanan, keamanan, dan respons pelayanan." },
      { q: "Bagaimana jika ada masalah dalam perjalanan?", a: "Ajukan pengaduan melalui halaman Pengaduan dengan menyertakan nomor order Anda, atau hubungi Admin via WhatsApp. Admin akan menindaklanjuti sesuai kewenangan platform." },
    ],
  },
];

export default function FAQPage() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: GROUPS.flatMap((g) => g.items).map((f) => ({
      "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <div className="min-h-screen bg-background">
      <Seo
        path="/faq"
        title="FAQ Travele — Pertanyaan Umum Seputar Travel & Pemesanan"
        description="Jawaban seputar pemesanan, pembayaran, driver, komisi, rating, dan layanan travel door to door & charter drop off Travele."
        schema={faqSchema}
      />
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="font-mono-terminal text-xs uppercase tracking-widest text-primary">Pusat Bantuan</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Pertanyaan yang Sering Diajukan</h1>
        <p className="mt-2 text-muted-foreground">Jawaban singkat seputar pemesanan, pembayaran, driver, dan layanan Travele.</p>

        {GROUPS.map((g, gi) => (
          <div key={gi} className="mt-8" data-testid={`faq-group-${gi}`}>
            <h2 className="mb-2 font-display text-xl font-bold">{g.title}</h2>
            <Accordion type="single" collapsible className="border border-border bg-card px-4">
              {g.items.map((f, i) => (
                <AccordionItem key={i} value={`g${gi}-${i}`} data-testid={`faq-${gi}-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}

        <div className="mt-12 border border-primary/40 bg-primary/5 p-8 text-center">
          <h2 className="font-display text-xl font-bold">Masih ada pertanyaan?</h2>
          <p className="mt-1 text-sm text-muted-foreground">Tim Admin Travele siap membantu Anda.</p>
          <a data-testid="faq-wa-btn" href={`https://wa.me/${WA_ADMIN}?text=${encodeURIComponent("Halo Admin Travele, saya ingin bertanya.")}`} target="_blank" rel="noopener noreferrer">
            <button className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#25D366] px-6 py-3 font-bold text-black transition-transform hover:-translate-y-0.5">
              <MessageCircle className="h-4 w-4" /> Chat Admin WhatsApp
            </button>
          </a>
        </div>

        <div className="mt-8 text-center text-sm"><Link to="/" className="text-primary hover:underline">← Kembali ke Beranda</Link></div>
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
